import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Trash2,
  FileText,
  FileWarning,
  Loader2,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Search,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

// function attachmentUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/menudocuments/${filename}`;
// }

function attachmentUrl(filename) {
  if (!filename) return null;
  const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/menudocuments/${fileName}`;
}

export default function ManageMainMenu() {
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [allNames, setAllNames] = useState([]);

  // Filters
  const [nameQ, setNameQ] = useState("");
  const [statusQ, setStatusQ] = useState("");

  const [toast, setToast] = useState(null);

  // ---------------------------------------------------------
  // Toast
  // ---------------------------------------------------------
  const showToast = (type, message) => {
    setToast({ type, message });

    setTimeout(() => {
      setToast(null);
    }, 2600);
  };

  // ---------------------------------------------------------
  // Show message coming from Add/Edit page
  // ---------------------------------------------------------
  useEffect(() => {
    if (location.state?.message) {
      showToast(
        location.state.type || "success",
        location.state.message
      );

      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [location, navigate]);

  // ---------------------------------------------------------
  // Fetch all menu names
  // ---------------------------------------------------------
  const fetchAllNames = async () => {
    try {
      const res = await adminApi.get("/admin/main-menu");

      setAllNames(
        (res.data.items || []).map((i) => ({
          page_id: i.page_id,
          page_name: i.page_name,
        }))
      );
    } catch (err) {
      console.error("Could not load menu name list:", err);
    }
  };

  // ---------------------------------------------------------
  // Fetch menu items
  // ---------------------------------------------------------
  const fetchItems = async (overrides = {}) => {
    setLoading(true);
    setError("");

    const params = {
      name: overrides.nameQ ?? nameQ,
      status: overrides.statusQ ?? statusQ,
    };

    try {
      const res = await adminApi.get("/admin/main-menu", {
        params,
      });

      setItems(res.data.items || []);
      setSelected([]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Could not load main menu contents."
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------
  useEffect(() => {
    fetchAllNames();
    fetchItems();
  }, []);

  // ---------------------------------------------------------
  // Search
  // ---------------------------------------------------------
  const handleSearch = (e) => {
    e.preventDefault();
    fetchItems();
  };

  // ---------------------------------------------------------
  // Reset
  // ---------------------------------------------------------
  const handleReset = () => {
    setNameQ("");
    setStatusQ("");

    fetchItems({
      nameQ: "",
      statusQ: "",
    });
  };

  // ---------------------------------------------------------
  // Select all
  // ---------------------------------------------------------
  const toggleSelectAll = () => {
    setSelected(
      selected.length === items.length
        ? []
        : items.map((i) => i.page_id)
    );
  };

  // ---------------------------------------------------------
  // Select one
  // ---------------------------------------------------------
  const toggleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  // ---------------------------------------------------------
  // Status toggle
  // 1 = Active
  // 2 = Inactive
  // ---------------------------------------------------------
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();

    const turningOn = item.page_status !== 1;

    const ok = window.confirm(
      `Are you sure you want to change the status to ${
        turningOn ? "Active" : "Inactive"
      }?`
    );

    if (!ok) return;

    try {
      const res = await adminApi.patch(
        `/admin/main-menu/${item.page_id}/status`
      );

      setItems((prev) =>
        prev.map((i) =>
          i.page_id === item.page_id
            ? {
                ...i,
                page_status: res.data.page_status,
              }
            : i
        )
      );

      showToast(
        "success",
        `Menu status changed to ${
          res.data.page_status === 1
            ? "Active"
            : "Inactive"
        }.`
      );
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message ||
          "Could not update status."
      );
    }
  };

  // ---------------------------------------------------------
  // Bulk delete
  // ---------------------------------------------------------
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;

    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected record(s)? This cannot be undone.`
    );

    if (!ok) return;

    setBulkDeleting(true);

    try {
      const res = await adminApi.post(
        "/admin/main-menu/bulk-delete",
        {
          ids: selected,
        }
      );

      showToast(
        "success",
        res.data.message ||
          "Selected records deleted successfully."
      );

      setSelected([]);

      await fetchItems();
      await fetchAllNames();
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message ||
          "Could not delete selected records."
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const allSelected =
    items.length > 0 &&
    selected.length === items.length;

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------
  return (
    <div className="h-full flex flex-col">

      {/* =====================================================
          TOOLBAR
      ====================================================== */}
      <div className="shrink-0 mb-2">

        <div className="flex items-center justify-between gap-3 mb-2">

          {/* Title */}
          <div>
            <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
              Manage Menu Contents
            </h3>
          </div>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="flex flex-wrap items-center gap-3"
          >

            {/* Menu Name */}
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
                Menu Name:
              </label>

              <select
                value={nameQ}
                onChange={(e) =>
                  setNameQ(e.target.value)
                }
                className="min-w-[100px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4]"
                style={{
                  background: "#fff",
                }}
              >
                <option value="">
                  -- All --
                </option>

                {allNames.map((n) => (
                  <option
                    key={n.page_id}
                    value={n.page_id}
                  >
                    {n.page_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
                Status:
              </label>

              <select
                value={statusQ}
                onChange={(e) =>
                  setStatusQ(e.target.value)
                }
                className="min-w-[130px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                style={{
                  background: "#fff",
                }}
              >
                <option value="">
                  -- All --
                </option>

                <option value="1">
                  Active
                </option>

                <option value="2">
                  Inactive
                </option>
              </select>
            </div>

            {/* Search / Reset */}
            <div className="flex items-center gap-1.5">

              <button
                type="submit"
                className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
              >
                <Search size={13} />
                Search
              </button>

              <button
                type="button"
                onClick={handleReset}
                title="Reset filters"
                className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-[13px] px-1.5 py-1.5 rounded-lg transition"
                style={{
                  background: "#fff",
                }}
              >
                <RotateCcw size={13} />
              </button>

            </div>

          </form>

          {/* Add / Delete */}
          <div className="flex items-center gap-2">

            <AnimatePresence>
              {selected.length > 0 && (
                <motion.button
                  initial={{
                    opacity: 0,
                    x: 8,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: 8,
                  }}
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
                >
                  {bulkDeleting ? (
                    <Loader2
                      size={15}
                      className="animate-spin"
                    />
                  ) : (
                    <Trash2 size={15} />
                  )}

                  Delete Selected ({selected.length})
                </motion.button>
              )}
            </AnimatePresence>

            <button
              onClick={() =>
                navigate("/admin/contents/main-menu/add")
              }
              className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
            >
              <Plus size={15} />
              Add New
            </button>

          </div>

        </div>
      </div>

      {/* =====================================================
          TABLE
      ====================================================== */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2
              size={18}
              className="animate-spin"
            />
            Loading...
          </div>

        ) : error ? (

          /* Error */
          <div className="py-16 text-center text-sm text-red-500">
            {error}
          </div>

        ) : items.length === 0 ? (

          /* Empty */
          <div className="py-16 text-center">
            <FileText
              size={28}
              className="mx-auto text-slate-300 mb-2"
            />

            <p className="text-sm text-slate-400">
              No records yet — click "Add New" to create one.
            </p>
          </div>

        ) : (

          /* Table */
          <div className="flex-1 min-h-0 overflow-auto">

            <table className="w-full text-sm border-collapse">

              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-base">

                  <th className="th-colorful px-3 py-3 pl-3 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">
                    Menu Name
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">
                    Display Menu Link at
                  </th>

                  <th
                    className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    Attachment
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">
                    Sequence
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">
                    Is Contents?
                  </th>

                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2">
                    Status
                  </th>

                </tr>
              </thead>

              <tbody>

                <AnimatePresence initial={false}>

                  {items.map((it, i) => (

                    <motion.tr
                      key={it.page_id}
                      layout
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.2,
                        delay: i * 0.02,
                      }}
                      // onClick={() =>
                      //   navigate(
                      //     `/admin/contents/main-menu/edit/${it.page_id}`
                      //   )
                      // }
                      onClick={() =>
                          navigate(
                            `/admin/contents/main-menu/edit`,
                            {
                              state: {
                                page_id: it.page_id
                              }
                            }
                          )
                        }
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(it.page_id)
                          ? "bg-[#0EA5A4]/15"
                          : ""
                      }`}
                    >

                      {/* Checkbox */}
                      <td
                        className="border-r border-slate-200"
                        onClick={(e) =>
                          e.stopPropagation()
                        }
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(
                            it.page_id
                          )}
                          onChange={() =>
                            toggleSelectOne(
                              it.page_id
                            )
                          }
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>

                      {/* Menu Name */}
                      <td
                        className="text-slate-800 font-medium border-r border-slate-200"
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        {it.page_name}
                      </td>

                      {/* Display */}
                      <td
                        className="text-slate-500 border-r border-slate-200"
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        {it.display_label}
                      </td>

                      {/* Attachment */}
                      <td
                        className="border-r border-slate-200"
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {it.attachment ? (

                          <a
                            href={attachmentUrl(
                              it.attachment
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) =>
                              e.stopPropagation()
                            }
                            className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                          >
                            <div
                              style={{
                                border:
                                  "0.5px solid #999",
                                padding: "7px",
                                marginTop: "3px",
                                marginBottom: "3px",
                                borderRadius: "7px",
                              }}
                            >
                              <i
                                style={{
                                  color: "green",
                                }}
                                className="fa fa-eye"
                                aria-hidden="true"
                              />
                            </div>
                          </a>

                        ) : (

                          <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs">
                            <FileWarning size={14} />
                            None
                          </span>

                        )}
                      </td>

                      {/* Sequence */}
                      <td
                        className="text-slate-500 border-r border-slate-200"
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        {it.seq_id}
                      </td>

                      {/* Contents */}
                      <td
                        className="text-slate-500 border-r border-slate-200"
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        {it.ischecked
                          ? "Yes"
                          : "No"}
                      </td>

                      {/* Status */}
                      <td
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        <div className="flex items-center justify-center">

                          <motion.button
                            whileTap={{
                              scale: 0.9,
                            }}
                            onClick={(e) =>
                              handleToggleStatus(
                                it,
                                e
                              )
                            }
                            title={
                              it.page_status === 1
                                ? "Active — click to deactivate"
                                : "Inactive — click to activate"
                            }
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                              it.page_status === 1
                                ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                            }`}
                          >
                            {it.page_status === 1 ? (
                              <CheckCircle2 size={15} />
                            ) : (
                              <XCircle size={15} />
                            )}

                            {it.page_status === 1
                              ? "Active"
                              : "Inactive"}
                          </motion.button>

                        </div>
                      </td>

                    </motion.tr>

                  ))}

                </AnimatePresence>

              </tbody>

            </table>

          </div>

        )}

      </div>

      {/* =====================================================
          TOAST
      ====================================================== */}
      <AnimatePresence>

        {toast && (

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: 20,
            }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success"
                ? "bg-[#0B2545] text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>

        )}

      </AnimatePresence>

    </div>
  );
}